import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Wallet, PlusCircle } from 'lucide-react';
import { userService } from '../services/userService';
import { membershipService } from '../services/membershipService';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { formatShortDate, formatCurrency } from '../../utils/formatters';

const STATUS_LABEL = {
  GREEN: { label: 'Оплачено', className: 'text-primary-700 bg-primary-100' },
  YELLOW: { label: 'Есть задолженность', className: 'text-amber-700 bg-amber-100' },
  RED: { label: 'Просрочка > 6 мес.', className: 'text-red-700 bg-red-100' },
  NONE: { label: 'Не участник', className: 'text-gray-600 bg-gray-100' },
};

const PAYMENT_STATUS_LABEL = {
  PENDING: { label: 'На проверке', className: 'text-amber-700 bg-amber-100' },
  CONFIRMED: { label: 'Подтверждён', className: 'text-primary-700 bg-primary-100' },
  REJECTED: { label: 'Отклонён', className: 'text-red-700 bg-red-100' },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const toUnix = (dateStr) =>
  dateStr ? Math.floor(new Date(`${dateStr}T00:00:00`).getTime() / 1000) : 0;

export default function UserFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    phone: '',
    iin: '',
  });
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [manualAmount, setManualAmount] = useState('2000');
  const [manualPaidAt, setManualPaidAt] = useState(todayStr());
  const [manualMemberSince, setManualMemberSince] = useState(todayStr());
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState('');

  const fetchMembership = useCallback(async () => {
    setMembershipLoading(true);
    try {
      const data = await membershipService.getUserMembership(id);
      setMembership(data);
    } catch (error) {
      console.error('Error fetching membership:', error);
    } finally {
      setMembershipLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
    fetchMembership();
  }, [id, fetchMembership]);

  const fetchUser = async () => {
    try {
      const user = await userService.getById(id);
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || '',
        phone: user.phone || '',
        iin: user.iin || '',
      });
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setFetching(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Имя обязательно';
    if (!formData.last_name.trim()) newErrors.last_name = 'Фамилия обязательна';
    if (!formData.email.trim()) newErrors.email = 'Email обязателен';
    if (!formData.username.trim()) newErrors.username = 'Имя пользователя обязательно';
    if (formData.iin && !/^\d{12}$/.test(formData.iin)) {
      newErrors.iin = 'ИИН должен содержать ровно 12 цифр';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await userService.update(id, formData);
      navigate('/admin/users');
    } catch (error) {
      console.error('Error saving user:', error);
      setErrors({
        submit: error.response?.data?.error || 'Ошибка сохранения. Попробуйте ещё раз.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddManualPayment = async (e) => {
    e.preventDefault();
    const amount = Number(manualAmount);
    if (!amount || amount <= 0) {
      setManualError('Укажите сумму больше нуля');
      return;
    }
    if (!membership?.is_member && !manualMemberSince) {
      setManualError('Укажите дату вступления — с неё будут считаться начисления');
      return;
    }

    setManualSubmitting(true);
    setManualError('');
    try {
      await membershipService.addManualPayment(id, {
        amount,
        paidAt: toUnix(manualPaidAt),
        memberSince: !membership?.is_member ? toUnix(manualMemberSince) : 0,
      });
      setManualAmount('2000');
      await fetchMembership();
    } catch (error) {
      console.error('Error adding manual payment:', error);
      setManualError(error.response?.data?.error || 'Не удалось добавить платёж.');
    } finally {
      setManualSubmitting(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const status = STATUS_LABEL[membership?.status] || STATUS_LABEL.NONE;
  const payments = [...(membership?.payments || [])].reverse();

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад к пользователям
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Редактирование пользователя</h1>

        {errors.submit && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Имя"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              error={errors.first_name}
              placeholder="Введите имя"
            />

            <Input
              label="Фамилия"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              error={errors.last_name}
              placeholder="Введите фамилию"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
              placeholder="email@example.com"
            />

            <Input
              label="Имя пользователя"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              error={errors.username}
              placeholder="username"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Телефон"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={errors.phone}
              placeholder="+7 700 000 00 00"
            />

            <Input
              label="ИИН"
              value={formData.iin}
              onChange={(e) => setFormData({ ...formData, iin: e.target.value })}
              error={errors.iin}
              placeholder="123456789012"
              maxLength={12}
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/admin/users')}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              Сохранить
            </Button>
          </div>
        </form>
      </div>

      {/* Membership & manual payments */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-bold text-gray-900">Членство и взносы</h2>
        </div>

        {membershipLoading ? (
          <div className="animate-pulse h-24 bg-gray-100 rounded-lg" />
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${status.className}`}>
                {status.label}
              </span>
              {membership?.is_member && (
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <span>Начислено: <strong className="text-gray-900">{formatCurrency(membership.accrued)}</strong></span>
                  <span>Оплачено: <strong className="text-gray-900">{formatCurrency(membership.paid)}</strong></span>
                  <span>Задолженность: <strong className="text-gray-900">{formatCurrency(membership.debt)}</strong></span>
                  <span>Оплачено по: <strong className="text-gray-900">{formatShortDate(membership.paid_until)}</strong></span>
                </div>
              )}
            </div>

            {/* Manual payment form — for migrating members who already paid before
                this feature existed (any amount, any date, incl. months paid ahead). */}
            <form
              onSubmit={handleAddManualPayment}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6"
            >
              <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                <PlusCircle className="h-4 w-4" />
                Добавить платёж вручную
              </p>
              <div className={`grid grid-cols-1 gap-3 ${membership?.is_member ? 'sm:grid-cols-3' : 'sm:grid-cols-3'}`}>
                <Input
                  label="Сумма, ₸"
                  type="number"
                  min="1"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="2000"
                />
                <Input
                  label="Дата платежа"
                  type="date"
                  value={manualPaidAt}
                  onChange={(e) => setManualPaidAt(e.target.value)}
                />
                {!membership?.is_member && (
                  <Input
                    label="Дата вступления"
                    type="date"
                    value={manualMemberSince}
                    onChange={(e) => setManualMemberSince(e.target.value)}
                  />
                )}
              </div>
              {!membership?.is_member && (
                <p className="text-xs text-gray-500 mt-2">
                  Дата вступления нужна, чтобы правильно считать начисления за прошлые месяцы —
                  например, если человек уже платил взносы раньше или сразу за несколько месяцев вперёд.
                </p>
              )}
              {manualError && <p className="text-red-600 text-sm mt-2">{manualError}</p>}
              <div className="mt-3">
                <Button type="submit" size="sm" loading={manualSubmitting}>
                  Добавить
                </Button>
              </div>
            </form>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Сумма</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Чек</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((p) => {
                    const st = PAYMENT_STATUS_LABEL[p.status] || PAYMENT_STATUS_LABEL.PENDING;
                    return (
                      <tr key={p.id}>
                        <td className="px-4 py-2">{formatShortDate(p.created_at)}</td>
                        <td className="px-4 py-2 font-medium">{formatCurrency(p.amount)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${st.className}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {p.receipt_url ? (
                            <a
                              href={p.receipt_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 underline"
                            >
                              Открыть
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                        Платежей пока нет
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
